import bugsnag from "@bugsnag/js";
import classNames from "classnames";
import filter from "lodash/filter";
import map from "lodash/map";
import noop from "lodash/noop";
import range from "lodash/range";
import React, { FunctionComponent, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import SelectionProviderBrowserPhoto from "../../../../components/molecules/selection/SelectionProviderBrowserPhoto";
import { ReactComponent as IconArrowLeft } from "../../../../icons/chevron-left.svg";
import BaseSelectionProvider from "../../../../providers/selection/BaseSelectionProvider";
import BaseUploadProvider from "../../../../providers/upload/BaseUploadProvider";
import {
  SelectionProviderConnect as SelectionProviderConnectType,
  SelectionProviderDisconnect as SelectionProviderDisconnectType,
} from "../../../../types/customization/configuration";
import {
  SelectionAlbum,
  SelectionPhoto,
  SelectionProvider,
  UserInfos,
} from "../../../../types/selection/selection";
import { isPhotoInPicturesList } from "../../../../utils/selection/finders";
import { imageUrlFromSelectionPhoto } from "../../../../utils/selection/photo";
import SelectionPagination from "../../../atoms/selection/SelectionPagination";
import SelectionProviderDisconnect from "../../../molecules/selection/SelectionProviderDisconnect";
import "./style.scss";

interface Props {
  albumsToIgnore?: string[];
  onAlbumOpened?: (opened: string | null) => void;
  onDataFetch?: (hasData: boolean) => void;
  onSelect?: (selectionPhoto: SelectionPhoto) => void;
  pageKey?: string;
  provider: SelectionProvider;
  providerInstance: BaseSelectionProvider;
  selectedPhotoKeys?: Array<{ key: string; providerRef: string }>;
  selectionPhotos?: SelectionPhoto[];
  selectionProviderConnect?: SelectionProviderConnectType;
  selectionProviderDisconnect?: SelectionProviderDisconnectType;
  uploadProvider?: BaseUploadProvider;
  uploadVersion?: number;
}

const SelectionProviderBrowser: FunctionComponent<Props> = ({
  albumsToIgnore = [],
  onAlbumOpened,
  onDataFetch,
  onSelect,
  pageKey,
  provider,
  providerInstance,
  selectedPhotoKeys = [],
  selectionPhotos = [],
  selectionProviderConnect,
  selectionProviderDisconnect,
  uploadProvider,
  uploadVersion = 0,
}) => {
  const intl = useIntl();
  const [loading, setLoading] = useState<boolean>(true);
  const [currentAlbum, setCurrentAlbum] = useState<SelectionAlbum | null>(null);

  const [albums, setAlbums] = useState<SelectionAlbum[]>([]);
  const [photos, setPhotos] = useState<SelectionPhoto[]>([]);
  const [userInfos, setUserInfos] = useState<UserInfos>();

  const [pagination, setPagination] = useState<{
    display: boolean;
    onClick: () => void;
  }>({
    display: false,
    onClick: noop,
  });

  const providerSelectionPhotos = useMemo(() => {
    return filter(selectionPhotos, (selectionPhoto) => selectionPhoto.provider === provider);
  }, [provider, selectionPhotos]);

  const resetPagination = (hasMore: boolean) => {
    if (!hasMore) {
      setPagination({ display: false, onClick: noop });
    }
  };

  const filterAlbums = (albumsList: SelectionAlbum[], ignoreList: string[]) =>
    albumsList.filter((album) => !ignoreList.includes(album.ref));

  useEffect(() => {
    if (albums.length > 0 && albumsToIgnore.length > 0) {
      setAlbums((albumsValue) => filterAlbums(albumsValue, albumsToIgnore));
    }
  }, [albumsToIgnore]);

  useEffect(() => {
    let isStillSubscribed = true;
    if (providerInstance.hasUserInfos()) {
      providerInstance
        .getUserInfos()
        .then((userInfosReceived) => {
          setUserInfos(userInfosReceived);
        })
        .catch((error: { response?: { status?: number } }) => {
          if (error?.response?.status === 401 && selectionProviderConnect) {
            selectionProviderConnect(provider).catch((e: Error) => {
              bugsnag.notify(e);
              // todo error message connection failed
            });
          }
        });
    }
    if (providerInstance.hasAlbums()) {
      setLoading(true);
      if (currentAlbum === null) {
        providerInstance
          .getAlbums()
          .then((albumsReceived) => {
            if (isStillSubscribed) {
              const albumsToDisplay = filterAlbums(albumsReceived, albumsToIgnore);
              setAlbums(albumsToDisplay);
              setPhotos([]);
              if (onDataFetch) {
                onDataFetch(albumsReceived.length > 0);
              }
              if (providerInstance.hasMoreAlbums()) {
                setPagination({
                  display: true,
                  onClick: () => {
                    providerInstance
                      .getMoreAlbums()
                      .then((moreAlbumsReceived) => {
                        setAlbums(moreAlbumsReceived);
                        resetPagination(providerInstance.hasMoreAlbums());
                      })
                      .catch((e: Error) => {
                        bugsnag.notify(e);
                        // todo error message
                      });
                  },
                });
              } else {
                setPagination({ display: false, onClick: noop });
              }
              setLoading(false);
            }
          })
          .catch((error: { response?: { status?: number } }) => {
            if (error.response?.status === 401 && selectionProviderConnect) {
              selectionProviderConnect(provider).catch((e: Error) => {
                bugsnag.notify(e);
                // todo error message
              });
            }
          });
      } else if (currentAlbum.ref === "my-selection") {
        setLoading(false);
        // we render manually
      } else {
        providerInstance
          .getAlbumPhotos(currentAlbum.ref)
          .then((photosReceived) => {
            if (isStillSubscribed) {
              setPhotos(photosReceived);
              if (onDataFetch) {
                onDataFetch(photosReceived.length > 0);
              }
              if (providerInstance.hasMoreAlbumPhotos(currentAlbum.ref)) {
                setPagination({
                  display: true,
                  onClick: () => {
                    providerInstance
                      .getMoreAlbumPhotos(currentAlbum.ref)
                      .then((morePhotosReceived) => {
                        setPhotos(morePhotosReceived);
                        resetPagination(providerInstance.hasMoreAlbumPhotos(currentAlbum.ref));
                      })
                      .catch((e: Error) => {
                        bugsnag.notify(e);
                        // todo handle error message
                      });
                  },
                });
              } else {
                setPagination({ display: false, onClick: noop });
              }
              setLoading(false);
            }
          })
          .catch((e: Error) => {
            bugsnag.notify(e);
            // todo handle error message
          });
      }
    } else {
      setLoading(true);
      providerInstance
        .getPhotos()
        .then((photosReceived) => {
          if (isStillSubscribed) {
            setPhotos(photosReceived);
            if (onDataFetch) {
              onDataFetch(photosReceived.length > 0);
            }
            if (providerInstance.hasMorePhotos()) {
              setPagination({
                display: true,
                onClick: () => {
                  providerInstance
                    .getMorePhotos()
                    .then((morePhotosReceived) => {
                      setPhotos(morePhotosReceived);
                      resetPagination(providerInstance.hasMorePhotos());
                    })
                    .catch((e: Error) => {
                      bugsnag.notify(e);
                      // todo handle error message
                    });
                },
              });
            } else {
              setPagination({ display: false, onClick: noop });
            }
            setLoading(false);
          }
        })
        .catch((e: Error) => {
          bugsnag.notify(e);
          // todo handle error message
        });
    }

    return () => {
      isStillSubscribed = false;
    };
  }, [
    currentAlbum,
    onDataFetch,
    provider,
    providerInstance,
    selectionProviderConnect,
    setAlbums,
    setLoading,
    setPhotos,
    uploadVersion,
  ]);

  const isPhotoSelected = useMemo(() => {
    return (selectionPhoto: SelectionPhoto) => {
      return isPhotoInPicturesList(selectionPhoto, selectedPhotoKeys);
    };
  }, [selectedPhotoKeys]);

  if (providerInstance.hasAlbums() && currentAlbum === null) {
    const renderedAlbums = [...albums];

    if (providerSelectionPhotos.length > 0 && process.env.NODE_ENV === "development") {
      renderedAlbums.unshift({
        count: providerSelectionPhotos.length,
        coverPictureUrl: imageUrlFromSelectionPhoto(providerSelectionPhotos[0], "full"),
        name: `${intl.formatMessage({
          id: "kustom_SelectionProviderBrowser_selection",
        })}`,
        ref: "my-selection",
      });
    }

    if (!loading && renderedAlbums.length === 0) {
      if (providerInstance.hasUserInfos() && userInfos && selectionProviderDisconnect) {
        return (
          <SelectionProviderDisconnect
            userInfos={userInfos}
            selectionProviderDisconnect={selectionProviderDisconnect}
            provider={provider}
          />
        );
      }
    }

    return (
      <div className="SelectionProviderBrowser">
        {providerInstance.hasUserInfos() && userInfos && selectionProviderDisconnect ? (
          <SelectionProviderDisconnect
            userInfos={userInfos}
            selectionProviderDisconnect={selectionProviderDisconnect}
            provider={provider}
          />
        ) : null}
        <div className="SelectionProviderBrowser-content">
          <div className="SelectionProviderBrowser-albums">
            {loading
              ? map(range(20), (i) => (
                  <div key={i} className="SelectionProviderBrowser-loadingAlbum" />
                ))
              : null}

            {!loading
              ? map(renderedAlbums, (album) => {
                  const navigate = () => {
                    setCurrentAlbum(album);
                    if (onAlbumOpened) {
                      onAlbumOpened(album.name);
                    }
                  };

                  return (
                    <div
                      key={album.ref}
                      className={classNames("SelectionProviderBrowser-album", {
                        "SelectionProviderBrowser-album--mySelection": album.ref === "my-selection",
                      })}
                      onClick={navigate}
                      style={{
                        backgroundImage: `url(${album.coverPictureUrl})`,
                      }}
                    >
                      <div className="SelectionProviderBrowser-albumName">{album.name}</div>
                    </div>
                  );
                })
              : null}
          </div>
        </div>
        {pagination.display ? <SelectionPagination onClick={pagination.onClick} /> : null}
      </div>
    );
  }

  if ((providerInstance.hasAlbums() && currentAlbum !== null) || !providerInstance.hasAlbums()) {
    const renderedPhotos =
      currentAlbum && currentAlbum.ref === "my-selection" ? providerSelectionPhotos : photos;
    const onAlbumClose = () => {
      setCurrentAlbum(null);
      if (onAlbumOpened) {
        onAlbumOpened(null);
      }
    };

    if (!loading && renderedPhotos.length === 0) {
      return null;
    }

    return (
      <div className="SelectionProviderBrowser">
        {providerInstance.hasAlbums() ? (
          <div className="SelectionProviderBrowser-header">
            <span className="SelectionProviderBrowser-header-action" onClick={onAlbumClose}>
              <span className="SelectionProviderBrowser-header-action-back">
                <IconArrowLeft />
                {intl.formatMessage({
                  id: "kustom_SelectionProviderBrowser_back",
                })}
              </span>
              <span className="SelectionProviderBrowser-header-action-name">
                {currentAlbum && currentAlbum.name}
              </span>
            </span>
          </div>
        ) : null}
        <div className="SelectionProviderBrowser-content">
          <div className="SelectionProviderBrowser-photos">
            {loading
              ? map(range(20), (i) => (
                  <div key={i} className="SelectionProviderBrowser-loadingPhoto" />
                ))
              : null}

            {!loading
              ? map(renderedPhotos, (selectionPhoto) => (
                  <SelectionProviderBrowserPhoto
                    key={selectionPhoto.key}
                    onSelect={onSelect}
                    pageKey={pageKey}
                    selected={isPhotoSelected(selectionPhoto)}
                    selectionPhoto={selectionPhoto}
                    uploadProvider={uploadProvider}
                  />
                ))
              : null}
          </div>
        </div>
        {pagination.display ? <SelectionPagination onClick={pagination.onClick} /> : null}
      </div>
    );
  }

  return null;
};

export default SelectionProviderBrowser;
